import { formatPhone, type PublicUser } from "@rhema/client";
import { Avatar, CheckIcon, cn, ListRow, SearchField } from "@rhema/ui";
import { useState } from "react";
import { toneFor } from "../data/view";
import { usePhoneEnabled } from "../session/features";
import { useContacts } from "./useContacts";

/** Whether a contact matches a filter, by name or by the digits of their number. */
export function matchesPerson(person: PublicUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (person.display_name.toLowerCase().includes(q)) return true;
  const digits = q.replace(/\D/g, "");
  return digits.length >= 3 && Boolean(person.phone?.replace(/\D/g, "").includes(digits));
}

/** Who someone is, under their name: their number when you can see it. */
export function personDetail(person: PublicUser): string {
  return person.phone ? formatPhone(person.phone) : person.about;
}

/**
 * Your contacts, filtered by name or number as you type. Rhema has no directory of people, so the
 * list only ever holds your own contacts.
 */
export function PeoplePicker({
  selected = [],
  exclude = [],
  onPick,
  multiple = false,
  autoFocus = true,
  emptyText = "You have no contacts on Rhema yet.",
}: {
  selected?: string[];
  exclude?: string[];
  onPick: (person: PublicUser) => void;
  multiple?: boolean;
  autoFocus?: boolean;
  /** Shown when you have no contacts to pick from. */
  emptyText?: string;
}) {
  const [query, setQuery] = useState("");
  const phone = usePhoneEnabled();
  const { contacts } = useContacts();
  const available = (contacts ?? []).filter((p) => !exclude.includes(p.id) && !p.is_blocked);
  const visible = available
    .filter((p) => matchesPerson(p, query))
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  return (
    <div className="flex min-h-0 flex-col">
      <SearchField
        label="Filter contacts"
        placeholder={phone ? "Name or number" : "Name"}
        value={query}
        onChange={setQuery}
        autoFocus={autoFocus}
        className="mt-4"
      />
      <h3 className="m-0 mt-3 px-1 text-[15px] font-bold">{phone ? "On Rhema" : "Your contacts"}</h3>
      <ul aria-label={phone ? "Contacts on Rhema" : "Your contacts"} className="m-0 mt-1 list-none p-0">
        {visible.map((p) => {
          const on = selected.includes(p.id);
          const detail = personDetail(p);
          return (
            <li key={p.id}>
              <ListRow
                onClick={() => onPick(p)}
                selected={multiple ? on : undefined}
                className={cn("rounded-tile px-2", on && "bg-blue-tint")}
                leading={<Avatar name={p.display_name} src={p.avatar_url} tone={toneFor(p.id)} size={42} />}
                title={p.display_name}
                subtitle={detail ? <span className="truncate">{detail}</span> : undefined}
                trailing={
                  multiple && on ? (
                    <CheckIcon size={18} strokeWidth={2.6} className="text-blue" label="Selected" />
                  ) : undefined
                }
              />
            </li>
          );
        })}
        {contacts && visible.length === 0 && (
          <li className="px-2 py-5 text-sm text-ink-secondary">
            {available.length ? "No contacts match." : emptyText}
          </li>
        )}
      </ul>
    </div>
  );
}
